import { TSupportMessage } from '../../services/resources/supportChat/types'
import { useState } from 'react'
import { Download, FileText, Sparkles, X } from 'lucide-react'

const IMAGE_FORMATS = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg', 'heic']

/**
 * Fundo da área de mensagens: pontilhado discreto sobre um tom quase branco
 * puxado para o oliva da marca. O cinza liso deixava as bolhas brancas sem
 * contraste — com a textura, a bolha "descola" do fundo sem borda pesada.
 */
export const CHAT_SURFACE = 'bg-[#f7f8f3] bg-[radial-gradient(#e2e6d6_1px,transparent_1px)] [background-size:18px_18px]'

/**
 * Imagem é para ser vista, não baixada: se o anexo for imagem, a bolha mostra a
 * própria imagem. Qualquer outro formato vira um cartão de arquivo.
 */
const isImage = (url: string, format?: string | null) => {
  if (format && IMAGE_FORMATS.includes(format.toLowerCase().replace('.', ''))) return true

  const extension = url.split('?')[0].split('.').pop()?.toLowerCase()

  return !!extension && IMAGE_FORMATS.includes(extension)
}

const fileNameFromUrl = (url: string) => decodeURIComponent(url.split('?')[0].split('/').pop() ?? 'arquivo')

const formatSize = (bytes?: number | null) => {
  if (!bytes) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`

  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}


const URL_PATTERN = /(https?:\/\/[^\s<]+)/g

const linkify = (text: string, onDark: boolean) =>

  text.split(URL_PATTERN).map((piece, index) => {
    if (index % 2 === 0) return piece


    const trailing = piece.match(/[.,;:!?)\]]+$/)?.[0] ?? ''
    const url = trailing ? piece.slice(0, -trailing.length) : piece

    return (
      <span key={`${index}-${url}`}>
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          className={`font-medium underline underline-offset-2 ${
            onDark ? 'text-white hover:opacity-80' : 'text-primary-800 hover:text-primary-900'
          }`}>
          {url}
        </a>
        {trailing}
      </span>
    )
  })

const initials = (name?: string | null) =>
  (name ?? '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('') || '?'

/** Etiqueta de dia entre as mensagens, como no WhatsApp. */
export const DaySeparator = ({ label }: { label: string }) => (
  <div className="my-3 flex justify-center">
    <span className="rounded-full bg-white/90 px-3 py-1 text-[11px] font-medium text-neutral-500 shadow-sm ring-1 ring-black/5 backdrop-blur">
      {label}
    </span>
  </div>
)

const Avatar = ({ message }: { message: TSupportMessage }) => {
  if (message.sender_type === 'bot') {
    return (
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white shadow-sm">
        <Sparkles size={15} />
      </span>
    )
  }

  return (
    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-100 text-[11px] font-semibold text-primary-900 shadow-sm">
      {initials(message.sender_name)}
    </span>
  )
}

const Attachment = ({
  url,
  format,
  sizeBytes,
  onDark,
  onOpenImage
}: {
  url: string
  format?: string | null
  sizeBytes?: number | null
  onDark: boolean
  onOpenImage: (url: string) => void
}) => {
  if (isImage(url, format)) {
    return (
      <button
        type="button"
        onClick={() => onOpenImage(url)}
        className="block cursor-zoom-in overflow-hidden rounded-xl border-0 bg-transparent p-0">
        <img
          src={url}
          alt="Anexo"
          loading="lazy"
          className="max-h-60 w-auto max-w-full rounded-xl object-contain transition hover:opacity-90"
        />
      </button>
    )
  }

  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className={`group flex items-center gap-3 rounded-xl px-3 py-2 text-xs no-underline transition ${
        onDark
          ? 'bg-white/15 text-white hover:bg-white/25'
          : 'bg-neutral-50 text-neutral-700 ring-1 ring-neutral-200 hover:bg-neutral-100'
      }`}>
      <span
        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
          onDark ? 'bg-white/20' : 'bg-white text-primary-800 shadow-sm'
        }`}>
        <FileText size={17} />
      </span>
      <span className="flex min-w-0 flex-col">
        <span className="max-w-[180px] truncate font-medium">{fileNameFromUrl(url)}</span>
        {sizeBytes ? (
          <span className={onDark ? 'text-white/70' : 'text-neutral-400'}>{formatSize(sizeBytes)}</span>
        ) : null}
      </span>
      <Download
        size={15}
        className={`shrink-0 transition group-hover:translate-y-0.5 ${onDark ? 'text-white/80' : 'text-neutral-400'}`}
      />
    </a>
  )
}

type MessageBubbleProps = {
  message: TSupportMessage
  /** `true` quando a mensagem é do próprio paciente (fica à direita). */
  isOwn: boolean
  /**
   * `false` quando a mensagem continua um bloco do mesmo remetente — aí o nome
   * e o avatar não se repetem e a bolha fica colada na anterior, como em
   * qualquer chat.
   */
  showSender?: boolean
}

const MessageBubble = ({ message, isOwn, showSender = true }: MessageBubbleProps) => {
  const [zoomedImage, setZoomedImage] = useState<string | null>(null)

  const attachments = Object.values(message.attachments ?? {})
  const isBot = message.sender_type === 'bot'

  const bubbleStyle = (() => {
    if (isOwn) return 'bg-primary-800 text-white shadow-md shadow-black/10'
    if (isBot) return 'bg-white text-neutral-800 shadow-sm ring-1 ring-violet-200'

    return 'bg-white text-neutral-800 shadow-sm ring-1 ring-black/5'
  })()

  // O "rabinho" só na primeira bolha do bloco: é ela que diz de quem é a fala.
  const tail = (() => {
    if (!showSender) return ''

    return isOwn ? 'rounded-tr-md' : 'rounded-tl-md'
  })()

  const time = new Date(message.created_at).toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit'
  })

  return (
    <>
      <div
        className={`flex w-full items-start gap-2 ${isOwn ? 'justify-end' : 'justify-start'} ${
          showSender ? 'mt-3 first:mt-0' : ''
        }`}>
        {/* Espaço do avatar mesmo sem avatar: o bloco fica alinhado numa coluna só. */}
        {!isOwn && (showSender ? <Avatar message={message} /> : <span className="w-8 shrink-0" />)}

        <div className={`flex max-w-[78%] flex-col gap-1 ${isOwn ? 'items-end' : 'items-start'}`}>
          {!isOwn && showSender && (
            <span className="flex items-center gap-1.5 px-1 text-[11px] font-semibold text-neutral-500">
              {isBot ? 'Bia' : (message.sender_name ?? 'Atendente')}
              {/* {isBot && (
                <span className="rounded-full bg-violet-100 px-1.5 py-px text-[9px] font-bold uppercase tracking-wide text-violet-700">
                  Bia
                </span>
              )} */}
            </span>
          )}

          <div className={`rounded-2xl px-3.5 py-2 ${tail} ${bubbleStyle}`}>
            {message.content && (
              <p className="m-0 whitespace-pre-wrap break-words text-sm leading-relaxed">
                {linkify(message.content, isOwn)}
              </p>
            )}

            {attachments.length > 0 && (
              <div className={`flex flex-col gap-2 ${message.content ? 'mt-2' : ''}`}>
                {attachments.map((attachment) => (
                  <Attachment
                    key={attachment.url}
                    url={attachment.url}
                    format={attachment.format}
                    sizeBytes={attachment.size_bytes}
                    onDark={isOwn}
                    onOpenImage={setZoomedImage}
                  />
                ))}
              </div>
            )}

            <span
              className={`mt-0.5 block text-right text-[10px] tabular-nums ${isOwn ? 'text-white/70' : 'text-neutral-400'}`}>
              {time}
            </span>
          </div>
        </div>
      </div>

      {/* Lightbox: imagem pequena na bolha, grande quando clica. */}
      {zoomedImage && (
        <button
          type="button"
          aria-label="Fechar imagem"
          onClick={() => setZoomedImage(null)}
          className="fixed inset-0 z-50 flex cursor-zoom-out items-center justify-center border-0 bg-black/80 p-8 backdrop-blur-sm">
          <span className="absolute right-5 top-5 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white">
            <X size={20} />
          </span>
          <img
            src={zoomedImage}
            alt="Anexo ampliado"
            className="max-h-full max-w-full rounded-lg object-contain shadow-2xl"
          />
        </button>
      )}
    </>
  )
}

export default MessageBubble
